import { GlTooltipDirective, GlBadge, GlButton, GlButtonGroup, GlDisclosureDropdown, GlDisclosureDropdownItem, GlDisclosureDropdownGroup, GlFormCheckbox, GlLoadingIcon, GlAnimatedChevronRightDownIcon } from '@gitlab/ui';
import { escape } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import { keysFor, MR_TOGGLE_REVIEW } from '~/behaviors/shortcuts/keybindings';
import { keyboardShortcutsDisabled } from '~/behaviors/shortcuts/shortcuts_disabled';
import SafeHtml from '~/vue_shared/directives/safe_html';
import { scrollToElement } from '~/lib/utils/scroll_utils';
import { truncateSha } from '~/lib/utils/text_utility';
import { sanitize } from '~/lib/dompurify';
import { __, s__, sprintf } from '~/locale';
import ClipboardButton from '~/vue_shared/components/clipboard_button.vue';
import { createFileUrl, fileContentsId } from '~/diffs/components/diff_row_utils';
import { useLegacyDiffs } from '~/diffs/stores/legacy_diffs';
import { useNotes } from '~/notes/store/legacy_notes';
import { useCodeReview } from '~/diffs/stores/code_review';
import { DIFF_FILE_AUTOMATIC_COLLAPSE } from '../constants';
import diffsEventHub from '../event_hub';
import { DIFF_FILE_HEADER } from '../i18n';
import { collapsedType, isCollapsed } from '../utils/diff_file';
import DiffStats from './diff_stats.vue';
const createHotkeyHtml = (key)=>`<kbd class="flat gl-ml-1" aria-hidden=true>${key}</kbd>`;
export default {
    name: 'DiffFileHeader',
    components: {
        ClipboardButton,
        DiffStats,
        GlBadge,
        GlDisclosureDropdown,
        GlDisclosureDropdownItem,
        GlDisclosureDropdownGroup,
        GlButton,
        GlButtonGroup,
        GlFormCheckbox,
        GlLoadingIcon,
        GlAnimatedChevronRightDownIcon
    },
    directives: {
        GlTooltip: GlTooltipDirective,
        SafeHtml
    },
    i18n: {
        ...DIFF_FILE_HEADER,
        compareButtonLabel: __('Compare submodule commit revisions'),
        fileModeTooltip: __('File permissions')
    },
    props: {
        discussionPath: {
            type: String,
            required: false,
            default: ''
        },
        diffFile: {
            type: Object,
            required: true
        },
        collapsible: {
            type: Boolean,
            required: false,
            default: false
        },
        addMergeRequestButtons: {
            type: Boolean,
            required: false,
            default: false
        },
        expanded: {
            type: Boolean,
            required: false,
            default: true
        },
        canCurrentUserFork: {
            type: Boolean,
            required: true
        },
        viewDiffsFileByFile: {
            type: Boolean,
            required: false,
            default: false
        },
        showLocalFileReviews: {
            type: Boolean,
            required: false,
            default: false
        },
        reviewed: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'show-fork-message',
        'toggle-file'
    ],
    computed: {
        ...mapState(useLegacyDiffs, [
            'diffHasExpandedDiscussions',
            'diffHasDiscussions'
        ]),
        ...mapState(useNotes, [
            'getNoteableData'
        ]),
        diffContentIDSelector () {
            return fileContentsId(this.diffFile);
        },
        diffUrl () {
            return createFileUrl(this.diffFile);
        },
        titleLink () {
            if (this.diffFile.submodule) {
                return this.diffFile.submodule_tree_url || this.diffFile.submodule_link;
            }
            if (!this.discussionPath) {
                return this.diffUrl;
            }
            return this.discussionPath;
        },
        submoduleDiffCompareLinkText () {
            if (this.diffFile.submodule_compare) {
                const truncatedOldSha = escape(truncateSha(this.diffFile.submodule_compare.old_sha));
                const truncatedNewSha = escape(truncateSha(this.diffFile.submodule_compare.new_sha));
                return sprintf(__('Compare %{old_commit} to %{new_commit}'), {
                    old_commit: `<span class="commit-sha">${truncatedOldSha}</span>`,
                    new_commit: `<span class="commit-sha">${truncatedNewSha}</span>`
                }, false);
            }
            return null;
        },
        filePath () {
            if (this.diffFile.submodule) {
                return `${this.diffFile.file_path} @ ${truncateSha(this.diffFile.blob.id)}`;
            }
            if (this.diffFile.deleted_file) {
                return sprintf(__('%{filePath} deleted'), {
                    filePath: this.diffFile.file_path
                }, false);
            }
            return this.diffFile.file_path;
        },
        isUsingLfs () {
            return this.diffFile.stored_externally && this.diffFile.external_storage === 'lfs';
        },
        isCollapsed () {
            return isCollapsed(this.diffFile, {
                fileByFile: this.viewDiffsFileByFile
            });
        },
        viewFileButtonText () {
            const truncatedContentSha = escape(truncateSha(this.diffFile.content_sha));
            return sprintf(s__('MergeRequests|View file @ %{commitId}'), {
                commitId: truncatedContentSha
            }, false);
        },
        viewReplacedFileButtonText () {
            const truncatedBaseSha = escape(truncateSha(this.diffFile.diff_refs.base_sha));
            return sprintf(s__('MergeRequests|View replaced file @ %{commitId}'), {
                commitId: truncatedBaseSha
            });
        },
        gfmCopyText () {
            return `\`${this.diffFile.file_path}\``;
        },
        isFileRenamed () {
            return this.diffFile.renamed_file;
        },
        isModeChanged () {
            return this.diffFile.mode_changed;
        },
        expandDiffToFullFileTitle () {
            if (this.diffFile.isShowingFullFile) {
                return s__('MRDiff|Show changes only');
            }
            return s__('MRDiff|Show full file');
        },
        showEditButton () {
            return this.diffFile.blob?.readable_text && !this.diffFile.deleted_file && (this.diffFile.edit_path || this.diffFile.ide_edit_path);
        },
        isReviewable () {
            return Boolean(this.diffFile.code_review_id) && Boolean(this.diffFile.file_identifier_hash);
        },
        externalUrlLabel () {
            return sprintf(__('View on %{url}'), {
                url: this.diffFile.formatted_external_url
            });
        },
        labelToggleFile () {
            return this.expanded ? __('Hide file contents') : __('Show file contents');
        },
        showCommentButton () {
            return this.getNoteableData.current_user.can_create_note;
        },
        viewFileDropdownItem () {
            return {
                text: this.viewFileButtonText,
                href: this.diffFile.view_path,
                extraAttrs: {
                    target: '_blank'
                }
            };
        },
        editInSingleFileEditorDropdownItem () {
            return {
                text: __('Edit single file'),
                action: this.showForkMessage,
                href: this.diffFile.can_modify_blob ? this.diffFile.edit_path : undefined,
                extraAttrs: {
                    class: 'js-edit-blob'
                }
            };
        },
        openInWebIdeDropdownItem () {
            return {
                text: __('Open in Web IDE'),
                href: this.diffFile.ide_edit_path,
                extraAttrs: {
                    target: '_blank',
                    'data-testid': 'edit-in-ide-button',
                    class: 'js-ide-edit-blob'
                }
            };
        },
        viewReplacedFileDropdownItem () {
            return {
                text: this.viewReplacedFileButtonText,
                href: this.diffFile.replaced_view_path,
                extraAttrs: {
                    target: '_blank'
                }
            };
        },
        toggleDiscussionDropdownItem () {
            return {
                text: __('Hide comments on this file'),
                action: ()=>this.toggleFileDiscussionWrappers(this.diffFile)
            };
        },
        toggleDiffDropdownItem () {
            return {
                text: this.expandDiffToFullFileTitle,
                action: ()=>this.toggleFullDiff(this.diffFile.file_path),
                extraAttrs: {
                    disabled: this.diffFile.isLoadingFullFile
                }
            };
        }
    },
    methods: {
        ...mapActions(useLegacyDiffs, [
            'toggleFileDiscussionWrappers',
            'toggleFullDiff',
            'setCurrentFileHash',
            'setFileCollapsedByUser',
            'setFileForcedOpen',
            'toggleFileCommentForm'
        ]),
        fileReviewTooltip () {
            const { description } = MR_TOGGLE_REVIEW;
            const keys = keysFor(MR_TOGGLE_REVIEW);
            return keyboardShortcutsDisabled() ? description : sanitize(`${description} ${createHotkeyHtml(keys[0])}`);
        },
        handleToggleFile () {
            diffsEventHub.$emit('set-file-active', this.diffFile.file_hash);
            this.setFileForcedOpen({
                filePath: this.diffFile.file_path,
                forced: false
            });
            this.$emit('toggle-file');
        },
        showForkMessage () {
            if (this.canCurrentUserFork && !this.diffFile.can_modify_blob) {
                this.$emit('show-fork-message');
            }
        },
        handleFileNameClick (e) {
            const isLinkToOtherPage = this.diffFile.submodule_tree_url || this.diffFile.submodule_link || this.discussionPath;
            if (!isLinkToOtherPage) {
                e.preventDefault();
                const selector = this.diffContentIDSelector;
                scrollToElement(document.querySelector(selector));
                window.location.hash = selector;
                if (!this.viewDiffsFileByFile) {
                    this.setCurrentFileHash(this.diffFile.file_hash);
                }
            }
        },
        toggleReview (newReviewedStatus) {
            // this is the easiest way to hide an already open tooltip that triggers on focus
            document.activeElement.blur();
            const autoCollapsed = this.isCollapsed && collapsedType(this.diffFile) === DIFF_FILE_AUTOMATIC_COLLAPSE;
            const open = this.expanded;
            const closed = !open;
            const reviewed = newReviewedStatus;
            // diffFile.id is generated on the frontend, code_review_id is generated on the backend
            // we need to leave only code_review_id as our single source of truth
            // this is done to prevent legacy id from persisting in the local storage
            useCodeReview().removeId(this.diffFile.id);
            useCodeReview().setReviewed(this.diffFile.code_review_id, reviewed);
            if (reviewed && autoCollapsed) {
                this.setFileCollapsedByUser({
                    filePath: this.diffFile.file_path,
                    collapsed: true
                });
            }
            if (open && reviewed || closed && !reviewed) {
                this.setFileForcedOpen({
                    filePath: this.diffFile.file_path,
                    forced: false
                });
                this.$emit('toggle-file');
            }
        }
    }
};
